import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TEN_MINUTES_IN_MS } from '@protector/utils/constants';
import { DIGITAL_TRAP_NETWORKS_QUERY_KEY, DIGITAL_TRAP_NETWORK_TRAP_COUNTS_QUERY_KEY, NETWORK_NOTES_QUERY_KEY } from './constants.ts';
import type {
  DigitalTrapNetwork,
  FlightStagePredictions,
  NetworkNote,
  NetworkNoteRequest,
  RegionalOverviewGroupsResponse
} from './models.ts';
import {
  createManualTrap,
  deleteNetworkNote,
  getDigitalTrapNetworks,
  getDigitalTrapNetworkTrapCounts,
  getDigitalTrapsPredictions,
  getNetworkNotes,
  getRegionalOverviewGroups,
  saveNetworkNote
} from './services.ts';

export function useGetRegionalOverviewGroupsQuery(organizationId: string) {
  return useQuery<RegionalOverviewGroupsResponse | undefined>({
    queryKey: ['regionalOverviewGroups', organizationId],
    queryFn: () => getRegionalOverviewGroups(organizationId),
    enabled: !!organizationId
  });
}

export function useGetDigitalTrapsPredictionsQuery(organizationId: string, assetIds: string[]) {
  return useQuery<FlightStagePredictions[]>({
    queryKey: ['predictions', organizationId, assetIds],
    queryFn: () => getDigitalTrapsPredictions(organizationId, assetIds),
    enabled: !!organizationId && assetIds.length > 0
  });
}

export function useGetDigitalTrapNetworksQuery(organizationId: string, enabled = true) {
  return useQuery<DigitalTrapNetwork[]>({
    queryKey: [DIGITAL_TRAP_NETWORKS_QUERY_KEY, organizationId],
    queryFn: () => getDigitalTrapNetworks(organizationId),
    enabled: enabled && !!organizationId,
    staleTime: TEN_MINUTES_IN_MS
  });
}

export function useGetDigitalTrapNetworkTrapCountsQuery(organizationId: string, enabled = true) {
  return useQuery<Record<string, number>>({
    queryKey: [DIGITAL_TRAP_NETWORK_TRAP_COUNTS_QUERY_KEY, organizationId],
    queryFn: getDigitalTrapNetworkTrapCounts,
    enabled: enabled && !!organizationId,
    staleTime: TEN_MINUTES_IN_MS
  });
}

export function useGetNetworkNotesQuery(organizationId: string, enabled = true) {
  return useQuery<NetworkNote[]>({
    queryKey: [NETWORK_NOTES_QUERY_KEY, organizationId],
    queryFn: () => getNetworkNotes(organizationId),
    enabled: enabled && !!organizationId
  });
}

export function useSaveNetworkNoteMutation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ networkId, data }: { networkId: string; data: NetworkNoteRequest }) => saveNetworkNote(organizationId, networkId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [NETWORK_NOTES_QUERY_KEY, organizationId] })
  });
}

export function useDeleteNetworkNoteMutation(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (networkId: string) => deleteNetworkNote(organizationId, networkId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [NETWORK_NOTES_QUERY_KEY, organizationId] })
  });
}

export function useCreateManualTrapMutation() {
  return useMutation({ mutationFn: createManualTrap });
}
